'use client';

import { IT_INT } from '@slims/shared/constants';
import * as React from 'react';
import { useMemo, useState } from 'react';

// Extracted from members-registry-view: the consumption-profile questionnaire now
// lives in the member Dettagli page (add-socio is name-only). Byte-identical move.

const EMPTY_CONSUMPTION_Q = {
	// ── Obbligatorie - comuni ──────────────────────────────────────────────────
	consumoAnnuoKwh: '',
	// ── Obbligatorie P1 ────────────────────────────────────────────────────────
	rientroPranzo: '' as '' | 'si' | 'no',
	tipoAC: '' as '' | 'split' | 'pompa_calore' | 'nessuno',
	// ── Obbligatorie P2a/P2b/P2c ──────────────────────────────────────────────
	orarioApertura: '',
	orarioChiusura: '',
	// ── Obbligatorie P2a ───────────────────────────────────────────────────────
	hasServerRoom: '' as '' | 'si' | 'no',
	tipoClimatizzazione: '' as '' | 'split' | 'hvac_centralizzato' | 'nessuno',
	// ── Obbligatorie P2b ───────────────────────────────────────────────────────
	aperturaWeekend: '' as '' | 'si' | 'no',
	aperturaDomenica: '' as '' | 'aperto' | 'chiuso' | 'ridotto',
	// ── Obbligatorie P2c ───────────────────────────────────────────────────────
	sottotipoRistorazione: '' as '' | 'bar' | 'ristorante' | 'alimentari' | 'supermercato',
	hasBanchiFrigo: '' as '' | 'si' | 'no',
	// ── Obbligatorie P3 ────────────────────────────────────────────────────────
	numTurni: '',
	hasCarichiNotturni: '' as '' | 'si' | 'no',
	orarioPausa: '',
	// ── Obbligatorie P4 ────────────────────────────────────────────────────────
	produzioneH24: '' as '' | 'si' | 'no',
	intensitaTurnoNotturno: '' as '' | 'piena' | 'ridotta',
	// ── Obbligatorie P3+P4 ─────────────────────────────────────────────────────
	potenzaContrattualeKw: '',
	// ── Opzionali P1 ───────────────────────────────────────────────────────────
	numPersone: '',
	oreACEstive: '',
	riscaldamentoElettrico: '' as '' | 'si' | 'no',
	veicoloElettrico: '' as '' | 'si' | 'no',
	presenzaGiornoFeriali: '' as '' | 'si' | 'no',
	// ── Opzionali P2a ──────────────────────────────────────────────────────────
	aperturaWeekendUfficio: '' as '' | 'si' | 'no',
	orarioWeekendUfficio: '',
	numPostazioniLavoro: '',
	riscaldamentoElettricoUfficio: '' as '' | 'elettrico' | 'gas' | 'teleriscaldamento',
	illuminazioneUfficio: '' as '' | 'led' | 'fluorescente' | 'mista',
	// ── Opzionali P2b ──────────────────────────────────────────────────────────
	tipoClimatizzazioneRetail: '' as '' | 'split' | 'hvac_centralizzato' | 'nessuno',
	superficieVendita: '',
	illuminazioneRetail: '' as '' | 'led' | 'fluorescente' | 'alogeni',
	chiusuraStagionale: '' as '' | 'si' | 'no',
	// ── Opzionali P2c ──────────────────────────────────────────────────────────
	aperturaFestivi: '' as '' | 'si' | 'no',
	cucinaProf: '' as '' | 'si' | 'no',
	tipoClimatizzazioneRisto: '' as '' | 'split' | 'hvac_centralizzato' | 'nessuno',
	superficieLocaleCoperti: '',
	// ── Opzionali P3 ───────────────────────────────────────────────────────────
	sabatoLavorativo: '' as '' | 'si_pieno' | 'si_ridotto' | 'no',
	tipoAvvioMacchinari: '' as '' | 'diretto' | 'inverter',
	settoreAtecoIndustriale: '',
	// ── Opzionali P4 ───────────────────────────────────────────────────────────
	fermataAnnualeSettimane: '',
	tipoProcesso: '' as '' | 'termico' | 'meccanico' | 'informatico' | 'frigorifero',
	hasCogenerazione: '' as '' | 'si' | 'no',
};

type ConsumptionQ = typeof EMPTY_CONSUMPTION_Q;

const CONSUMPTION_PROFILE_LABELS: Record<string, string> = {
	p1_residential: 'Residenziale (P1)',
	p2a_office: 'Uffici / PA (P2a)',
	p2b_retail: 'Commerciale Retail (P2b)',
	p2c_food: 'Ristorazione / Alimentare (P2c)',
	p3_industrial_2t: 'Industriale 2 turni (P3)',
	p4_industrial_h24: 'Industriale H24 (P4)',
};

function profileCategoryFromCode(code: string): '' | 'casa' | 'ufficio_comm' | 'industria' {
	if (code === 'p1_residential') return 'casa';
	if (code.startsWith('p2')) return 'ufficio_comm';
	if (code.startsWith('p3') || code.startsWith('p4')) return 'industria';
	return '';
}

function profileSubcatFromCode(code: string): string {
	if (code === 'p2a_office') return 'office_pa';
	if (code === 'p2b_retail') return 'retail';
	if (code === 'p2c_food') return 'food';
	if (code === 'p3_industrial_2t') return 'no_night';
	if (code === 'p4_industrial_h24') return 'yes_night';
	return '';
}

function validateConsumptionProfile(profile: string, q: ConsumptionQ): string[] {
	const missing: string[] = [];
	if (!profile) {
		missing.push('Tipologia di utenza');
		return missing;
	}
	if (profile === 'p1_residential') {
		if (!q.rientroPranzo) missing.push('Rientro a pranzo nei feriali');
		if (!q.tipoAC) missing.push('Presenza aria condizionata');
	}
	if (profile === 'p2a_office') {
		if (!q.orarioApertura) missing.push('Orario apertura');
		if (!q.orarioChiusura) missing.push('Orario chiusura');
		if (!q.hasServerRoom) missing.push('Presenza server room');
		if (!q.tipoClimatizzazione) missing.push('Tipo climatizzazione');
	}
	if (profile === 'p2b_retail') {
		if (!q.orarioApertura) missing.push('Orario apertura');
		if (!q.orarioChiusura) missing.push('Orario chiusura');
		if (!q.aperturaWeekend) missing.push('Apertura sabato');
		if (!q.aperturaDomenica) missing.push('Apertura domenica');
	}
	if (profile === 'p2c_food') {
		if (!q.sottotipoRistorazione) missing.push('Tipo di attività ristorazione');
		if (!q.orarioApertura) missing.push('Orario apertura');
		if (!q.orarioChiusura) missing.push('Orario chiusura');
		if (!q.hasBanchiFrigo) missing.push('Presenza banchi frigo');
	}
	if (profile === 'p3_industrial_2t') {
		if (!q.numTurni) missing.push('Numero turni');
		if (!q.hasCarichiNotturni) missing.push('Carichi notturni');
		if (!q.orarioPausa) missing.push('Orario pausa');
		if (!q.potenzaContrattualeKw) missing.push('Potenza contrattuale (kW)');
	}
	if (profile === 'p4_industrial_h24') {
		if (!q.produzioneH24) missing.push('Continuità produzione H24');
		if (!q.intensitaTurnoNotturno) missing.push('Intensità turno notturno');
		if (!q.potenzaContrattualeKw) missing.push('Potenza contrattuale (kW)');
	}
	return missing;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
	return (
		<div className="border-b border-border/50 pb-1 text-[11px] font-bold uppercase tracking-widest text-text-muted">
			{children}
		</div>
	);
}

function ConsumptionProfileSection({
	profile,
	onProfileChange,
	q,
	onQChange,
	showErrors = false,
}: {
	profile: string;
	onProfileChange: (code: string) => void;
	q: ConsumptionQ;
	onQChange: (q: ConsumptionQ) => void;
	showErrors?: boolean;
}) {
	// Helper - applica border rosso se showErrors è attivo e il campo è vuoto
	function err(val: string | undefined | null) {
		return showErrors && !val ? ' border-danger' : '';
	}
	const [pendingCategory, setPendingCategory] = useState<
		'' | 'casa' | 'ufficio_comm' | 'industria'
	>('');
	const [showOptional, setShowOptional] = useState(false);

	// Deriva la categoria dal profilo già impostato (es. modifica), altrimenti usa lo stato locale
	const category = profile ? profileCategoryFromCode(profile) : pendingCategory;
	const subcat = profileSubcatFromCode(profile);

	function handleCategoryChange(cat: string) {
		if (cat === 'casa') {
			onProfileChange('p1_residential');
			setPendingCategory('casa');
		} else if (cat === 'ufficio_comm' || cat === 'industria') {
			onProfileChange('');
			setPendingCategory(cat);
		} else {
			onProfileChange('');
			setPendingCategory('');
		}
	}

	function handleSubcatChange(sc: string) {
		const map: Record<string, string> = {
			office_pa: 'p2a_office',
			retail: 'p2b_retail',
			food: 'p2c_food',
			no_night: 'p3_industrial_2t',
			yes_night: 'p4_industrial_h24',
		};
		onProfileChange(map[sc] ?? '');
	}

	function setQ(field: keyof ConsumptionQ, value: string) {
		onQChange({ ...q, [field]: value });
	}

	// §8 - Validation rules (computed from current profile + answers)
	const warnings = useMemo(() => {
		type ConsumptionWarning = {
			id: string;
			type: 'warning' | 'suggestion' | 'error';
			message: string;
			action?: { label: string; profileCode: string };
		};
		const ws: ConsumptionWarning[] = [];
		if (!profile) return ws;

		const kwhNum = q.consumoAnnuoKwh ? parseFloat(q.consumoAnnuoKwh) : null;

		// §8.1 - Range tipico per profilo (avvisi se fuori range ±wide)
		const kwhRanges: Record<string, [number, number]> = {
			p1_residential: [500, 8000],
			p2a_office: [2000, 100000],
			p2b_retail: [3000, 150000],
			p2c_food: [5000, 250000],
			p3_industrial_2t: [10000, 500000],
			p4_industrial_h24: [30000, 2000000],
		};
		if (kwhNum !== null && kwhRanges[profile]) {
			const [lo, hi] = kwhRanges[profile];
			if (kwhNum < lo) {
				ws.push({
					id: 'kwh_low',
					type: 'warning',
					message: `Il consumo dichiarato (${kwhNum.toLocaleString('it-IT', IT_INT)} kWh/anno) è inferiore al minimo tipico per questo profilo (${lo.toLocaleString('it-IT', IT_INT)} kWh). Verificare il dato o considerare un profilo diverso.`,
				});
			} else if (kwhNum > hi) {
				ws.push({
					id: 'kwh_high',
					type: 'warning',
					message: `Il consumo dichiarato (${kwhNum.toLocaleString('it-IT', IT_INT)} kWh/anno) è superiore al massimo tipico per questo profilo (${hi.toLocaleString('it-IT', IT_INT)} kWh). Verificare il dato o considerare un profilo diverso.`,
				});
			}
		}

		// §8.2 - Trigger di incompatibilità profilo
		// Trigger 1: P4 ma produzione NON h24 → suggerisci P3
		if (profile === 'p4_industrial_h24' && q.produzioneH24 === 'no') {
			ws.push({
				id: 'p4_no_h24',
				type: 'suggestion',
				message:
					'La produzione non è continuativa h24: il profilo P3 (2 turni) potrebbe essere più accurato.',
				action: { label: 'Passa a P3', profileCode: 'p3_industrial_2t' },
			});
		}

		// Trigger 2: P3 senza carichi notturni → avviso baseline quasi G0
		if (profile === 'p3_industrial_2t' && q.hasCarichiNotturni === 'no') {
			ws.push({
				id: 'p3_no_night',
				type: 'warning',
				message:
					'Senza carichi notturni il profilo P3 si avvicina al profilo G0 generico. Valutare se il profilo Ufficio (P2a) sia più rappresentativo.',
			});
		}

		// Trigger 3: P2a con orario molto esteso (≤ 06:00 apertura e ≥ 22:00 chiusura) → anomalia
		if (profile === 'p2a_office' && q.orarioApertura && q.orarioChiusura) {
			if (q.orarioApertura <= '06:00' && q.orarioChiusura >= '22:00') {
				ws.push({
					id: 'p2a_extended',
					type: 'error',
					message:
						"L'orario dichiarato è quasi h24: anomalo per un ufficio. Verificare i dati o valutare profilo P3/P4.",
				});
			}
		}

		// Trigger 4: P2b aperto la domenica fino a tardi → suggerisci P2c
		if (
			profile === 'p2b_retail' &&
			q.aperturaDomenica !== 'chiuso' &&
			q.orarioChiusura &&
			q.orarioChiusura >= '22:00'
		) {
			ws.push({
				id: 'p2b_late_sunday',
				type: 'suggestion',
				message:
					'Apertura domenicale con chiusura serale tardiva: caratteristiche simili alla Ristorazione/GDO (P2c).',
				action: { label: 'Passa a P2c', profileCode: 'p2c_food' },
			});
		}

		// Trigger 5: P2c senza banchi frigo → suggerisci P2b o P2a
		if (profile === 'p2c_food' && q.hasBanchiFrigo === 'no') {
			ws.push({
				id: 'p2c_no_fridge',
				type: 'suggestion',
				message:
					'Senza banchi frigo il profilo Retail (P2b) o Ufficio (P2a) potrebbero essere più accurati.',
				action: { label: 'Passa a P2b', profileCode: 'p2b_retail' },
			});
		}

		// Trigger 6: P2c supermercato con kWh < 30 000 → fuori range
		if (
			profile === 'p2c_food' &&
			q.sottotipoRistorazione === 'supermercato' &&
			kwhNum !== null &&
			kwhNum < 30000
		) {
			ws.push({
				id: 'p2c_super_low',
				type: 'error',
				message: `Un supermercato con ${kwhNum.toLocaleString('it-IT', IT_INT)} kWh/anno sembra troppo basso. Verificare il consumo dichiarato (minimo tipico: 30.000 kWh).`,
			});
		}

		return ws;
	}, [profile, q]);

	return (
		<>
			<SectionLabel>Profilo di Consumo</SectionLabel>

			{/* Step 1 - categoria principale */}
			<FormRow label="Che tipo di utenza è? *">
				<select
					value={category}
					onChange={(e) => handleCategoryChange(e.target.value)}
					className={`${INPUT_CLASS}${err(category)}`}
				>
					<option value="">- seleziona -</option>
					<option value="casa">🏠 Casa / Abitazione</option>
					<option value="ufficio_comm">🏢 Ufficio / Attività commerciale</option>
					<option value="industria">🏭 Industria / Produzione</option>
				</select>
			</FormRow>

			{/* Step 1b - branching ufficio/comm */}
			{category === 'ufficio_comm' && (
				<FormRow label="Che tipo di attività è? *">
					<select
						value={subcat}
						onChange={(e) => handleSubcatChange(e.target.value)}
						className={`${INPUT_CLASS}${err(profile)}`}
					>
						<option value="">- seleziona -</option>
						<option value="office_pa">Ufficio, studio professionale, PA, scuola</option>
						<option value="retail">Negozio, showroom, retail, servizi</option>
						<option value="food">Bar, ristorante, pizzeria, alimentari, supermercato</option>
					</select>
				</FormRow>
			)}

			{/* Step 2 - branching industria */}
			{category === 'industria' && (
				<FormRow label="L'attività produttiva è operativa anche di notte (oltre le 22h)? *">
					<select
						value={subcat}
						onChange={(e) => handleSubcatChange(e.target.value)}
						className={`${INPUT_CLASS}${err(profile)}`}
					>
						<option value="">- seleziona -</option>
						<option value="no_night">No - fermiamo tra le 22h e le 6h</option>
						<option value="yes_night">Sì - produciamo su 3 turni o H24</option>
					</select>
				</FormRow>
			)}

			{/* Profilo risolto */}
			{profile && (
				<div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-[12px] font-semibold text-accent">
					Profilo assegnato: {CONSUMPTION_PROFILE_LABELS[profile] ?? profile}
				</div>
			)}

			{/* ── DOMANDE OBBLIGATORIE - P1 Residenziale ─────────────────────── */}
			{profile === 'p1_residential' && (
				<div className="space-y-3 rounded-lg border border-border bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande obbligatorie *
					</p>
					<FormRow label="I componenti del nucleo familiare rientrano a pranzo nei giorni feriali? *">
						<select
							value={q.rientroPranzo}
							onChange={(e) =>
								setQ('rientroPranzo', e.target.value as ConsumptionQ['rientroPranzo'])
							}
							className={`${INPUT_CLASS}${err(q.rientroPranzo)}`}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (picco 12-13h +12-15%)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					<FormRow label="È presente l'aria condizionata? *">
						<select
							value={q.tipoAC}
							onChange={(e) => setQ('tipoAC', e.target.value as ConsumptionQ['tipoAC'])}
							className={`${INPUT_CLASS}${err(q.tipoAC)}`}
						>
							<option value="">- seleziona -</option>
							<option value="split">Sì - split / climatizzatore</option>
							<option value="pompa_calore">Sì - pompa di calore</option>
							<option value="nessuno">No</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OBBLIGATORIE - P2a Uffici/PA ───────────────────────── */}
			{profile === 'p2a_office' && (
				<div className="space-y-3 rounded-lg border border-border bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande obbligatorie *
					</p>
					<div className="grid grid-cols-2 gap-3">
						<FormRow label="Orario apertura *">
							<input
								type="time"
								value={q.orarioApertura}
								onChange={(e) => setQ('orarioApertura', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioApertura)}`}
							/>
						</FormRow>
						<FormRow label="Orario chiusura *">
							<input
								type="time"
								value={q.orarioChiusura}
								onChange={(e) => setQ('orarioChiusura', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioChiusura)}`}
							/>
						</FormRow>
					</div>
					<FormRow label="Sono presenti server room, data room o UPS significativi? *">
						<select
							value={q.hasServerRoom}
							onChange={(e) =>
								setQ('hasServerRoom', e.target.value as ConsumptionQ['hasServerRoom'])
							}
							className={`${INPUT_CLASS}${err(q.hasServerRoom)}`}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (baseline notturna &gt; 10%)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					<FormRow label="Tipo di climatizzazione *">
						<select
							value={q.tipoClimatizzazione}
							onChange={(e) =>
								setQ('tipoClimatizzazione', e.target.value as ConsumptionQ['tipoClimatizzazione'])
							}
							className={`${INPUT_CLASS}${err(q.tipoClimatizzazione)}`}
						>
							<option value="">- seleziona -</option>
							<option value="hvac_centralizzato">HVAC centralizzato</option>
							<option value="split">Split / unità autonome</option>
							<option value="nessuno">Nessuno</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OBBLIGATORIE - P2b Retail ──────────────────────────── */}
			{profile === 'p2b_retail' && (
				<div className="space-y-3 rounded-lg border border-border bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande obbligatorie *
					</p>
					<div className="grid grid-cols-2 gap-3">
						<FormRow label="Orario apertura *">
							<input
								type="time"
								value={q.orarioApertura}
								onChange={(e) => setQ('orarioApertura', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioApertura)}`}
							/>
						</FormRow>
						<FormRow label="Orario chiusura *">
							<input
								type="time"
								value={q.orarioChiusura}
								onChange={(e) => setQ('orarioChiusura', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioChiusura)}`}
							/>
						</FormRow>
					</div>
					<FormRow label="Il negozio è aperto il sabato? *">
						<select
							value={q.aperturaWeekend}
							onChange={(e) =>
								setQ('aperturaWeekend', e.target.value as ConsumptionQ['aperturaWeekend'])
							}
							className={`${INPUT_CLASS}${err(q.aperturaWeekend)}`}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					<FormRow label="Apertura domenica / festivi *">
						<select
							value={q.aperturaDomenica}
							onChange={(e) =>
								setQ('aperturaDomenica', e.target.value as ConsumptionQ['aperturaDomenica'])
							}
							className={`${INPUT_CLASS}${err(q.aperturaDomenica)}`}
						>
							<option value="">- seleziona -</option>
							<option value="aperto">Aperto (profilo pieno ~70%)</option>
							<option value="ridotto">Aperto con orario ridotto</option>
							<option value="chiuso">Chiuso (baseline notturna)</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OBBLIGATORIE - P2c Ristorazione/Alimentare ─────────── */}
			{profile === 'p2c_food' && (
				<div className="space-y-3 rounded-lg border border-border bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande obbligatorie *
					</p>
					<FormRow label="Tipo di attività *">
						<select
							value={q.sottotipoRistorazione}
							onChange={(e) =>
								setQ(
									'sottotipoRistorazione',
									e.target.value as ConsumptionQ['sottotipoRistorazione'],
								)
							}
							className={`${INPUT_CLASS}${err(q.sottotipoRistorazione)}`}
						>
							<option value="">- seleziona -</option>
							<option value="bar">Bar / caffè (picco colazione 6-9h)</option>
							<option value="ristorante">Ristorante / pizzeria (doppio picco pranzo + cena)</option>
							<option value="alimentari">Alimentari / gastronomia (8-13h + 16-20h)</option>
							<option value="supermercato">Supermercato (8-21h, baseline frigo H24)</option>
						</select>
					</FormRow>
					<div className="grid grid-cols-2 gap-3">
						<FormRow label="Orario apertura *">
							<input
								type="time"
								value={q.orarioApertura}
								onChange={(e) => setQ('orarioApertura', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioApertura)}`}
							/>
						</FormRow>
						<FormRow label="Orario chiusura *">
							<input
								type="time"
								value={q.orarioChiusura}
								onChange={(e) => setQ('orarioChiusura', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioChiusura)}`}
							/>
						</FormRow>
					</div>
					<FormRow label="Sono presenti banchi frigoriferi, celle o armadi frigo? *">
						<select
							value={q.hasBanchiFrigo}
							onChange={(e) =>
								setQ('hasBanchiFrigo', e.target.value as ConsumptionQ['hasBanchiFrigo'])
							}
							className={`${INPUT_CLASS}${err(q.hasBanchiFrigo)}`}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (baseline notturna ~15-35%)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OBBLIGATORIE - P3 Industriale 2 turni ──────────────── */}
			{profile === 'p3_industrial_2t' && (
				<div className="space-y-3 rounded-lg border border-border bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande obbligatorie *
					</p>
					<div className="grid grid-cols-2 gap-3">
						<FormRow label="Numero di turni *">
							<input
								type="number"
								min="1"
								max="3"
								value={q.numTurni}
								onChange={(e) => setQ('numTurni', e.target.value)}
								className={`${INPUT_CLASS}${err(q.numTurni)}`}
								placeholder="Es. 2"
							/>
						</FormRow>
						<FormRow label="Orario pausa pranzo *">
							<input
								type="time"
								value={q.orarioPausa}
								onChange={(e) => setQ('orarioPausa', e.target.value)}
								className={`${INPUT_CLASS}${err(q.orarioPausa)}`}
							/>
						</FormRow>
					</div>
					<FormRow label="Sono presenti compressori aria, forni o refrigerazione attivi anche di notte? *">
						<select
							value={q.hasCarichiNotturni}
							onChange={(e) =>
								setQ('hasCarichiNotturni', e.target.value as ConsumptionQ['hasCarichiNotturni'])
							}
							className={`${INPUT_CLASS}${err(q.hasCarichiNotturni)}`}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (baseline notturna ≥ 60% del picco)</option>
							<option value="no">No (baseline notturna ridotta)</option>
						</select>
					</FormRow>
					<FormRow label="Potenza contrattuale (kW) *">
						<input
							type="number"
							value={q.potenzaContrattualeKw}
							onChange={(e) => setQ('potenzaContrattualeKw', e.target.value)}
							className={`${INPUT_CLASS}${err(q.potenzaContrattualeKw)}`}
							placeholder="Es. 100"
						/>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OBBLIGATORIE - P4 Industriale H24 ──────────────────── */}
			{profile === 'p4_industrial_h24' && (
				<div className="space-y-3 rounded-lg border border-border bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande obbligatorie *
					</p>
					<FormRow label="La produzione è effettivamente continua 365 giorni/anno? *">
						<select
							value={q.produzioneH24}
							onChange={(e) =>
								setQ('produzioneH24', e.target.value as ConsumptionQ['produzioneH24'])
							}
							className={`${INPUT_CLASS}${err(q.produzioneH24)}`}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì - H24, 365 gg (profilo confermato)</option>
							<option value="no">No - retrocedere a Profilo 3 (2 turni)</option>
						</select>
					</FormRow>
					<FormRow label="Il turno notturno ha la stessa intensità produttiva degli altri turni? *">
						<select
							value={q.intensitaTurnoNotturno}
							onChange={(e) =>
								setQ(
									'intensitaTurnoNotturno',
									e.target.value as ConsumptionQ['intensitaTurnoNotturno'],
								)
							}
							className={`${INPUT_CLASS}${err(q.intensitaTurnoNotturno)}`}
						>
							<option value="">- seleziona -</option>
							<option value="piena">Piena - stessa intensità (variazione ±15%)</option>
							<option value="ridotta">Ridotta - meno intensità termica (variazione ±5-10%)</option>
						</select>
					</FormRow>
					<FormRow label="Potenza contrattuale (kW) *">
						<input
							type="number"
							value={q.potenzaContrattualeKw}
							onChange={(e) => setQ('potenzaContrattualeKw', e.target.value)}
							className={`${INPUT_CLASS}${err(q.potenzaContrattualeKw)}`}
							placeholder="Es. 500"
						/>
					</FormRow>
				</div>
			)}

			{/* ── VALIDAZIONE POST-ONBOARDING (§8.1 + §8.2) ──────────────────── */}
			{profile && warnings.length > 0 && (
				<div className="space-y-2">
					{warnings.map((w) => (
						<div
							key={w.id}
							className={`rounded-lg border px-3 py-2 text-[12px] ${
								w.type === 'error'
									? 'border-danger/40 bg-danger/5 text-danger'
									: w.type === 'suggestion'
										? 'border-accent/30 bg-accent/5 text-text-primary'
										: 'border-warn/40 bg-warn/5 text-text-primary'
							}`}
						>
							<span className="mr-1">
								{w.type === 'error' ? '🔴' : w.type === 'suggestion' ? '💡' : '⚠️'}
							</span>
							{w.message}
							{w.action && (
								<button
									type="button"
									onClick={() => onProfileChange(w.action?.profileCode ?? '')}
									className="ml-2 rounded border border-current px-2 py-0.5 text-[11px] font-semibold transition-colors hover:bg-current/10"
								>
									{w.action.label}
								</button>
							)}
						</div>
					))}
				</div>
			)}

			{/* ── TOGGLE OPZIONALI ────────────────────────────────────────────── */}
			{profile && (
				<OptionalToggle showOptional={showOptional} onToggle={() => setShowOptional((v) => !v)} />
			)}

			{/* ── DOMANDE OPZIONALI - P1 Residenziale ────────────────────────── */}
			{showOptional && profile === 'p1_residential' && (
				<div className="space-y-3 rounded-lg border border-border/60 bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande opzionali - migliorano la precisione della curva
					</p>
					<FormRow label="F1 - Quante persone vivono nell'abitazione?">
						<input
							type="number"
							min="1"
							value={q.numPersone}
							onChange={(e) => setQ('numPersone', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 3"
						/>
					</FormRow>
					<FormRow label="F2 - Quante ore al giorno usano l'AC in estate (mediamente)?">
						<input
							type="number"
							min="0"
							max="24"
							value={q.oreACEstive}
							onChange={(e) => setQ('oreACEstive', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 6"
						/>
					</FormRow>
					<FormRow label="F3 - Il riscaldamento è elettrico? (pompa di calore, climatizzatore inverter)">
						<select
							value={q.riscaldamentoElettrico}
							onChange={(e) =>
								setQ(
									'riscaldamentoElettrico',
									e.target.value as ConsumptionQ['riscaldamentoElettrico'],
								)
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">
								Sì (pompa di calore / inverter) - aumenta baseline invernale
							</option>
							<option value="no">No (gas / gasolio / altro)</option>
						</select>
					</FormRow>
					<FormRow label="F4 - È presente un veicolo elettrico (auto o scooter)?">
						<select
							value={q.veicoloElettrico}
							onChange={(e) =>
								setQ('veicoloElettrico', e.target.value as ConsumptionQ['veicoloElettrico'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (picco ricarica notturna/serale 22h-6h)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					<FormRow label="F5 - C'è qualcuno in casa durante il giorno nei feriali?">
						<select
							value={q.presenzaGiornoFeriali}
							onChange={(e) =>
								setQ(
									'presenzaGiornoFeriali',
									e.target.value as ConsumptionQ['presenzaGiornoFeriali'],
								)
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">
								Sì (smart working, pensionati, bambini piccoli) - plateau 9-17h
							</option>
							<option value="no">No - calo pomeriggio confermato</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OPZIONALI - P2a Uffici/PA ──────────────────────────── */}
			{showOptional && profile === 'p2a_office' && (
				<div className="space-y-3 rounded-lg border border-border/60 bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande opzionali - migliorano la precisione della curva
					</p>
					<FormRow label="F1 - L'edificio è aperto anche il sabato?">
						<select
							value={q.aperturaWeekendUfficio}
							onChange={(e) =>
								setQ(
									'aperturaWeekendUfficio',
									e.target.value as ConsumptionQ['aperturaWeekendUfficio'],
								)
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					{q.aperturaWeekendUfficio === 'si' && (
						<FormRow label="F1b - Con quale orario il sabato?">
							<input
								type="text"
								value={q.orarioWeekendUfficio}
								onChange={(e) => setQ('orarioWeekendUfficio', e.target.value)}
								className={INPUT_CLASS}
								placeholder="Es. 8:00-13:00"
							/>
						</FormRow>
					)}
					<FormRow label="F2 - Numero approssimativo di postazioni lavoro o dipendenti">
						<input
							type="number"
							min="1"
							value={q.numPostazioniLavoro}
							onChange={(e) => setQ('numPostazioniLavoro', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 20"
						/>
					</FormRow>
					<FormRow label="F3 - Il riscaldamento invernale è elettrico o a gas/teleriscaldamento?">
						<select
							value={q.riscaldamentoElettricoUfficio}
							onChange={(e) =>
								setQ(
									'riscaldamentoElettricoUfficio',
									e.target.value as ConsumptionQ['riscaldamentoElettricoUfficio'],
								)
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="elettrico">
								Elettrico (pompa di calore) - alza baseline invernale diurna
							</option>
							<option value="gas">Gas / gasolio</option>
							<option value="teleriscaldamento">Teleriscaldamento</option>
						</select>
					</FormRow>
					<FormRow label="F4 - Illuminazione prevalente">
						<select
							value={q.illuminazioneUfficio}
							onChange={(e) =>
								setQ('illuminazioneUfficio', e.target.value as ConsumptionQ['illuminazioneUfficio'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="led">LED (consumo ridotto)</option>
							<option value="fluorescente">Fluorescente / neon</option>
							<option value="mista">Mista</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OPZIONALI - P2b Retail ─────────────────────────────── */}
			{showOptional && profile === 'p2b_retail' && (
				<div className="space-y-3 rounded-lg border border-border/60 bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande opzionali - migliorano la precisione della curva
					</p>
					<FormRow label="F1 - Tipo di climatizzazione">
						<select
							value={q.tipoClimatizzazioneRetail}
							onChange={(e) =>
								setQ(
									'tipoClimatizzazioneRetail',
									e.target.value as ConsumptionQ['tipoClimatizzazioneRetail'],
								)
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="hvac_centralizzato">HVAC centralizzato</option>
							<option value="split">Split / unità autonome</option>
							<option value="nessuno">Nessuno</option>
						</select>
					</FormRow>
					<FormRow label="F2 - Superficie di vendita approssimativa (m²)">
						<input
							type="number"
							min="1"
							value={q.superficieVendita}
							onChange={(e) => setQ('superficieVendita', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 150"
						/>
					</FormRow>
					<FormRow label="F3 - Illuminazione prevalente">
						<select
							value={q.illuminazioneRetail}
							onChange={(e) =>
								setQ('illuminazioneRetail', e.target.value as ConsumptionQ['illuminazioneRetail'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="led">LED</option>
							<option value="fluorescente">Fluorescente / neon</option>
							<option value="alogeni">Faretti alogeni (consumo elevato)</option>
						</select>
					</FormRow>
					<FormRow label="F4 - C'è un periodo di chiusura stagionale (es. agosto)?">
						<select
							value={q.chiusuraStagionale}
							onChange={(e) =>
								setQ('chiusuraStagionale', e.target.value as ConsumptionQ['chiusuraStagionale'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (verrà escluso dal calcolo annuale)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OPZIONALI - P2c Ristorazione/Alimentare ────────────── */}
			{showOptional && profile === 'p2c_food' && (
				<div className="space-y-3 rounded-lg border border-border/60 bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande opzionali - migliorano la precisione della curva
					</p>
					<FormRow label="F1 - L'attività è aperta la domenica e nei festivi?">
						<select
							value={q.aperturaFestivi}
							onChange={(e) =>
								setQ('aperturaFestivi', e.target.value as ConsumptionQ['aperturaFestivi'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (profilo domenicale pieno)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					<FormRow label="F2 - È presente una cucina professionale? (forni, piastre, lavastoviglie industriale)">
						<select
							value={q.cucinaProf}
							onChange={(e) => setQ('cucinaProf', e.target.value as ConsumptionQ['cucinaProf'])}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (amplifica i picchi di servizio pranzo/cena)</option>
							<option value="no">No</option>
						</select>
					</FormRow>
					<FormRow label="F3 - Tipo di climatizzazione">
						<select
							value={q.tipoClimatizzazioneRisto}
							onChange={(e) =>
								setQ(
									'tipoClimatizzazioneRisto',
									e.target.value as ConsumptionQ['tipoClimatizzazioneRisto'],
								)
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="hvac_centralizzato">HVAC centralizzato</option>
							<option value="split">Split / unità autonome</option>
							<option value="nessuno">Nessuno</option>
						</select>
					</FormRow>
					<FormRow label="F4 - Superficie del locale (m²) o numero di coperti">
						<input
							type="text"
							value={q.superficieLocaleCoperti}
							onChange={(e) => setQ('superficieLocaleCoperti', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 80 m² oppure 40 coperti"
						/>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OPZIONALI - P3 Industriale 2 turni ─────────────────── */}
			{showOptional && profile === 'p3_industrial_2t' && (
				<div className="space-y-3 rounded-lg border border-border/60 bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande opzionali - migliorano la precisione della curva
					</p>
					<FormRow label="F1 - Il sabato è lavorativo?">
						<select
							value={q.sabatoLavorativo}
							onChange={(e) =>
								setQ('sabatoLavorativo', e.target.value as ConsumptionQ['sabatoLavorativo'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si_pieno">Sì - turno pieno</option>
							<option value="si_ridotto">Sì - turno unico ridotto</option>
							<option value="no">No - fermo sabato</option>
						</select>
					</FormRow>
					<FormRow label="F2 - I macchinari principali hanno avvio diretto o con inverter/soft starter?">
						<select
							value={q.tipoAvvioMacchinari}
							onChange={(e) =>
								setQ('tipoAvvioMacchinari', e.target.value as ConsumptionQ['tipoAvvioMacchinari'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="inverter">Inverter / soft starter (rampa mattutina graduale)</option>
							<option value="diretto">Avvio diretto (picco elevato in rampa 6-9h)</option>
						</select>
					</FormRow>
					<FormRow label="F3 - Settore ATECO / tipo di produzione prevalente">
						<input
							type="text"
							value={q.settoreAtecoIndustriale}
							onChange={(e) => setQ('settoreAtecoIndustriale', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 25.11 - strutture metalliche"
						/>
					</FormRow>
				</div>
			)}

			{/* ── DOMANDE OPZIONALI - P4 Industriale H24 ─────────────────────── */}
			{showOptional && profile === 'p4_industrial_h24' && (
				<div className="space-y-3 rounded-lg border border-border/60 bg-background p-3">
					<p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
						Domande opzionali - migliorano la precisione della curva
					</p>
					<FormRow label="F1 - Esiste una fermata programmata annuale per manutenzione?">
						<input
							type="text"
							value={q.fermataAnnualeSettimane}
							onChange={(e) => setQ('fermataAnnualeSettimane', e.target.value)}
							className={INPUT_CLASS}
							placeholder="Es. 2 settimane ad agosto"
						/>
					</FormRow>
					<FormRow label="F2 - Tipo di processo prevalente">
						<select
							value={q.tipoProcesso}
							onChange={(e) => setQ('tipoProcesso', e.target.value as ConsumptionQ['tipoProcesso'])}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="termico">Termico (fonderie, forni, ceramica)</option>
							<option value="meccanico">Meccanico (lavorazioni, assemblaggio)</option>
							<option value="informatico">Informatico / data center</option>
							<option value="frigorifero">Frigorifero (logistica refrigerata)</option>
						</select>
					</FormRow>
					<FormRow label="F3 - È presente cogenerazione o autoproduzione termica?">
						<select
							value={q.hasCogenerazione}
							onChange={(e) =>
								setQ('hasCogenerazione', e.target.value as ConsumptionQ['hasCogenerazione'])
							}
							className={INPUT_CLASS}
						>
							<option value="">- seleziona -</option>
							<option value="si">Sì (trigenerazione / CHP) - riduce prelievo rete</option>
							<option value="no">No</option>
						</select>
					</FormRow>
				</div>
			)}

			{/* §8.3 - Le domande opzionali non compilate useranno il valore standard del profilo */}
			{showOptional && profile && (
				<p className="text-[11px] italic text-text-muted">
					Le domande opzionali non compilate non bloccano il salvataggio. I parametri mancanti
					verranno impostati al valore standard del profilo durante la generazione delle curve di
					carico.
				</p>
			)}
		</>
	);
}

function OptionalToggle({
	showOptional,
	onToggle,
}: {
	showOptional: boolean;
	onToggle: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onToggle}
			className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border/60 px-3 py-2 text-[12px] text-text-muted transition-colors hover:border-accent/40 hover:text-accent"
		>
			<span className="flex-1 text-left">
				{showOptional
					? '▲ Nascondi domande opzionali'
					: '▼ Mostra domande opzionali (migliorano la curva)'}
			</span>
		</button>
	);
}

function FormRow({
	label,
	error,
	children,
}: {
	label: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="space-y-1">
			<label className="text-[12px] font-semibold uppercase tracking-wider text-text-muted">
				{label}
			</label>
			{children}
			{error && <p className="mt-0.5 text-[12px] font-bold text-danger">{error}</p>}
		</div>
	);
}

const INPUT_CLASS =
	'w-full rounded-lg border border-border bg-background px-3 py-1.5 text-[13px] text-text-primary focus:border-accent focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-card/50';

export { ConsumptionProfileSection, EMPTY_CONSUMPTION_Q, validateConsumptionProfile };
export type { ConsumptionQ };
